import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CalendarDaysIcon,
  DocumentIcon,
  MapIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  TrashIcon,
  PhotoIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { supabase, deleteSession, getFolderAncestors } from '../utils/supabaseClient';
import FusionMap from '../components/FusionMap';
import { PayAnalysisEngine } from '../utils/PayAnalysisEngine';
import { PayAnalysisContent } from '../components/PayAnalysisContent';
import Breadcrumb from '../components/Breadcrumb';
import AnalysisPage from './AnalysisPage';
import { formatStationFromFeet, parseStationString, filterSessionData } from '../utils/filterSessionData';
import SessionFiles from '../components/SessionFiles';
import SitePhotosTab from '../components/SitePhotosTab';
import SessionDetails from '../components/SessionDetails';
import ALRMapView from '../components/ALRMapView';
import ALRMapModal from '../components/ALRMapModal';
import { useKmlData } from '../hooks/useKmlData';
import ShareTab from '../components/ShareTab';
import { ShareIcon } from '@heroicons/react/24/outline';

const SessionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'map';

  // Data state
  const [session, setSession] = useState(null);
  const [project, setProject] = useState(null);
  const [projectConfig, setProjectConfig] = useState(null);
  const [segments, setSegments] = useState([]);
  const [events, setEvents] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Folder path state for breadcrumb
  const [folderPath, setFolderPath] = useState([]);

  // Sibling sessions for breadcrumb dropdown
  const [siblingSessions, setSiblingSessions] = useState([]);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // UI state
  const [payAnalysisAvailable, setPayAnalysisAvailable] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [payAnalysis, setPayAnalysis] = useState(null);
  const [payAnalysisLoading, setPayAnalysisLoading] = useState(false);
  const [showHQMode, setShowHQMode] = useState(false);

  const [kmlUrl, setKmlUrl] = useState(null);

  const [isAlrModalOpen, setIsAlrModalOpen] = useState(false);
  const [selectedAlrEvent, setSelectedAlrEvent] = useState(null);

  const [targetSegmentId, setTargetSegmentId] = useState(null);

  // Filtering validation state
  const [stationValidation, setStationValidation] = useState({
    start: { isValid: true, error: null },
    end: { isValid: true, error: null }
  });

  const tabs = [
    { id: 'map', name: 'Map', icon: MapIcon },
    { id: 'analysis', name: 'Analysis', icon: ChartBarIcon },
    // { id: 'pay-analysis', name: 'Pay Table', icon: CurrencyDollarIcon },
    { id: 'files', name: 'Files', icon: DocumentIcon },
    // { id: 'photos', name: 'Photos', icon: PhotoIcon },
    { id: 'details', name: 'Details', icon: InformationCircleIcon },
    { id: 'share', name: 'Share', icon: ShareIcon },
  ];

  // Add disabled state to tabs
  const tabsWithState = tabs.map(tab => ({
    ...tab,
    disabled: tab.id === 'pay-analysis' && !payAnalysisAvailable,
    badge: tab.id === 'pay-analysis' && !payAnalysisAvailable && !loading ? '!' : null
  }));

  // Build breadcrumb items with folder path
  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        label: 'Projects',
        onClick: () => navigate('/projects')
      },
      {
        label: project?.name || 'Project',
        icon: BriefcaseIcon,
        emphasis: true,
        loading: !project,
        onClick: project?.id ? () => navigate(`/projects/${project.id}`) : null
      }
    ];

    // Add folder path items
    folderPath.forEach((folder, index) => {
      items.push({
        label: folder.name,
        onClick: () => navigate(`/projects/${project.id}?folder=${folder.id}`)
      });
    });

    // Build siblings list for dropdown
    const siblings = siblingSessions.map(s => ({
      id: s.session_id,
      label: s.name || s.filename,
      current: s.session_id === sessionId,
      onClick: () => navigate(`/session/${s.session_id}`)
    }));

    // Add session as final item with siblings dropdown
    items.push({
      label: session?.name || session?.filename || 'Session',
      loading: loading && !session,
      current: true,
      siblings: siblings.length > 1 ? siblings : null, // Only show dropdown if there are multiple sessions
      backToParent: project?.id ? {
        label: 'Back to Project',
        onClick: () => navigate(`/projects/${project.id}${session?.folder_id ? `?folder=${session.folder_id}` : ''}`)
      } : null
    });

    return items;
  }, [project, session, folderPath, siblingSessions, sessionId, loading, navigate]);

  // Fetch folder ancestors when session loads
  useEffect(() => {
    const fetchFolderPath = async () => {
      if (session?.folder_id) {
        try {
          const ancestors = await getFolderAncestors(session.folder_id);
          setFolderPath(ancestors);
        } catch (err) {
          console.error('Error fetching folder ancestors:', err);
          setFolderPath([]);
        }
      } else {
        setFolderPath([]);
      }
    };
    fetchFolderPath();
  }, [session?.folder_id]);

  // Fetch sibling sessions (same folder) for breadcrumb dropdown
  useEffect(() => {
    const fetchSiblingSessions = async () => {
      if (!session?.project_id) return;

      try {
        let query = supabase
          .from('prototype_sessions')
          .select('session_id, name, filename')
          .eq('project_id', session.project_id)
          .order('name', { ascending: true });

        // Filter by same folder (or root if no folder)
        if (session.folder_id) {
          query = query.eq('folder_id', session.folder_id);
        } else {
          query = query.is('folder_id', null);
        }

        const { data, error } = await query;

        if (error) throw error;
        setSiblingSessions(data || []);
      } catch (err) {
        console.error('Error fetching sibling sessions:', err);
        setSiblingSessions([]);
      }
    };

    fetchSiblingSessions();
  }, [session?.project_id, session?.folder_id]);

  // Helper function to fetch all events using pagination
  const fetchAllEvents = async (sessionId) => {
    let allEvents = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;


    while (hasMore) {
      setLoadingProgress(`Loading events page ${page + 1}...`);
      
      const { data: events, error } = await supabase
        .from('prototype_events')
        .select('*')
        .eq('session_id', sessionId)
        .order('from_station_ft')
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (error) {
        console.error(`Error fetching events page ${page}:`, error);
        throw error;
      }

      
      if (events.length === 0) {
        hasMore = false;
      } else {
        allEvents = allEvents.concat(events);
        page++;
        
        if (page > 100) {
          console.warn('Reached maximum pagination limit for events (100 pages)');
          hasMore = false;
        }
        
        hasMore = events.length === pageSize;
      }
    }

    return allEvents;
  };

  // Helper function to fetch all segments using pagination
  const fetchAllSegments = async (sessionId) => {
    let allSegments = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;


    while (hasMore) {
      setLoadingProgress(`Loading segments page ${page + 1}...`);
      
      const { data: segments, error } = await supabase
        .from('prototype_segments')
        .select('*')
        .eq('session_id', sessionId)
        .order('from_station_ft')
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (error) {
        console.error(`Error fetching segments page ${page}:`, error);
        throw error;
      }

      
      if (segments.length === 0) {
        hasMore = false;
      } else {
        allSegments = allSegments.concat(segments);
        page++;
        
        if (page > 100) {
          console.warn('Reached maximum pagination limit for segments (100 pages)');
          hasMore = false;
        }
        
        hasMore = segments.length === pageSize;
      }
    }

    return allSegments;
  };


// Add KML data loading - single source of truth
const { kmlLoading, kmlError, iriProfileData, elevationProfileData, markers } = useKmlData(session);


  // Apply analysis range filtering
  const analysisRangeData = useMemo(() => {
    const startStation = session?.analysis_start_station;
    const endStation = session?.analysis_end_station;
    
    if (!startStation && !endStation) {
      return {
        segments,
        events,
        active: false,
        stats: null
      };
    }
    
    const result = filterSessionData({
      segments,
      events,
      rawStart: startStation,
      rawEnd: endStation,
      sessionBounds: {
        start_station_ft: session?.start_station_ft,
        end_station_ft: session?.end_station_ft
      }
    });
    
    return {
      segments: result.segments,
      events: result.events,
      active: true,
      stats: result.stats
    };
  }, [
    segments,
    events,
    session?.analysis_start_station,
    session?.analysis_end_station,
    session?.start_station_ft,
    session?.end_station_ft
  ]);

  const filteredSegments = analysisRangeData.segments;
  const filteredEvents = analysisRangeData.events;
  const analysisRangeActive = analysisRangeData.active;
  const filterStats = analysisRangeData.stats;

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      setError(null);
      setPayAnalysis(null);
      setPayAnalysisAvailable(false);
      setPayAnalysisLoading(false);
      setKmlUrl(null);
      setLoadingProgress('Loading session details...');

      const { data: sessionData, error: sessionError } = await supabase
        .from('prototype_sessions')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (sessionError) throw new Error(sessionError.message);
      setSession(sessionData);

      setEditValues({
        name: sessionData.name || '',
        operator: sessionData.operator || '',
        direction: sessionData.direction || '',
        lane_no: sessionData.lane_no || '',
        route_lrs_key: sessionData.route_lrs_key || '',
        run_date: formatDateForInput(sessionData.run_date) || '',
        profiler_model: sessionData.profiler_model || '',
        meta: typeof sessionData.meta === 'object' ? JSON.stringify(sessionData.meta, null, 2) : (sessionData.meta || ''),
        analysis_start_station: sessionData.analysis_start_station ? formatStationFromFeet(sessionData.analysis_start_station) : '',
        analysis_end_station: sessionData.analysis_end_station ? formatStationFromFeet(sessionData.analysis_end_station) : '',
      });

      setLoadingProgress('Loading session map data...');

      const kmlUrlPromise = sessionData.meta?.kml_path
        ? supabase.storage
            .from('session-files')
            .createSignedUrl(sessionData.meta.kml_path, 86400)
            .catch(error => ({ error }))
        : Promise.resolve(null);

      const projectPromise = supabase
        .from('projects')
        .select(`*, project_configurations(*, pay_tables(id, name, description))`)
        .eq('id', sessionData.project_id)
        .single();

      const boundsPromise = supabase.rpc(
        'get_prototype_session_bounds',
        { session_id_input: sessionId }
      );

      const [
        kmlUrlResult,
        projectResult,
        segmentsData,
        eventsData,
        boundsResult
      ] = await Promise.all([
        kmlUrlPromise,
        projectPromise,
        fetchAllSegments(sessionId),
        fetchAllEvents(sessionId),
        boundsPromise
      ]);

      if (kmlUrlResult?.error) {
        console.error('Error generating KML signed URL:', kmlUrlResult.error);
      } else if (kmlUrlResult?.data?.signedUrl) {
        setKmlUrl(kmlUrlResult.data.signedUrl);
      }

      const { data: projectData, error: projectError } = projectResult;

      if (projectError) {
        console.error('Error fetching project:', projectError);
      } else {
        setProject(projectData);
        
        let config = null;
        if (Array.isArray(projectData.project_configurations)) {
          config = projectData.project_configurations[0] || null;
        } else if (projectData.project_configurations && typeof projectData.project_configurations === 'object') {
          config = projectData.project_configurations;
        }
        setProjectConfig(config);
      }
      
      setSegments(segmentsData || []);
      setEvents(eventsData || []);
      
      if (sessionData?.meta?.events_count && (eventsData?.length || 0) !== sessionData.meta.events_count) {
        console.warn(`Event count mismatch: Expected ${sessionData.meta.events_count}, got ${eventsData?.length || 0}`);
      }
      
      if (sessionData?.meta?.segments_count && (segmentsData?.length || 0) !== sessionData.meta.segments_count) {
        console.warn(`Segment count mismatch: Expected ${sessionData.meta.segments_count}, got ${segmentsData?.length || 0}`);
      }

      if (boundsResult?.error) {
        console.error('Error loading map bounds:', boundsResult.error);
      } else if (boundsResult?.data?.length) {
        setMapBounds(boundsResult.data[0]);
      }

      let finalConfig = null;
      if (projectData) {
        finalConfig = Array.isArray(projectData.project_configurations) ? projectData.project_configurations[0] : projectData.project_configurations;
      }
      
      const hasConfig = finalConfig?.is_configured;
      const hasPayTable = finalConfig?.pay_table_id && finalConfig?.pay_tables;
      const hasSegments = segmentsData?.length > 0;
      setPayAnalysisAvailable(hasConfig && hasPayTable && hasSegments);

    } catch (err) {
      console.error('Error fetching session data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingProgress('');
    }
  };

  const runPayAnalysis = useCallback(async (segmentData, payTableName, configData) => {
    try {
      setPayAnalysisLoading(true);
      
      const dataToAnalyze = filteredSegments.length > 0 ? filteredSegments : segmentData;
      
      const engine = new PayAnalysisEngine(dataToAnalyze, payTableName, configData);
      const results = engine.generatePayAnalysis();
      
      setPayAnalysis(results);
    } catch (err) {
      console.error('Error running pay analysis:', err);
    } finally {
      setPayAnalysisLoading(false);
    }
  }, [filteredSegments]);

  useEffect(() => {
    const shouldLoadPayAnalysis = activeTab === 'analysis' || activeTab === 'pay-analysis';
    const payTableName = projectConfig?.pay_tables?.name;

    if (!shouldLoadPayAnalysis || !payAnalysisAvailable || !payTableName || segments.length === 0) {
      return;
    }

    runPayAnalysis(segments, payTableName, projectConfig);
  }, [activeTab, payAnalysisAvailable, segments, projectConfig, runPayAnalysis]);

  const handleAnalyzeSegment = (segmentId) => {
    setTargetSegmentId(segmentId);
    setSearchParams({ tab: 'analysis' }); // Switch to Analysis tab
  };

  const handleStationInputChange = (field, value) => {
    setEditValues(prev => ({ ...prev, [field]: value }));
    
    if (value.trim() === '') {
      setStationValidation(prev => ({
        ...prev,
        [field.split('_')[1]]: { isValid: true, error: null }
      }));
      return;
    }
    
    const validation = parseStationString(value);
    setStationValidation(prev => ({
      ...prev,
      [field.split('_')[1]]: validation
    }));
  };
  
  const handleClearAnalysisRange = async () => {
    try {
      const { error } = await supabase
        .from('prototype_sessions')
        .update({
          analysis_start_station: null,
          analysis_end_station: null
        })
        .eq('session_id', sessionId);

      if (error) throw error;

      setSession(prev => ({
        ...prev,
        analysis_start_station: null,
        analysis_end_station: null
      }));
    } catch (err) {
      console.error('Error clearing analysis range:', err);
      alert('Failed to clear analysis range: ' + err.message);
    }
  };
  
  const getAnalysisDistance = () => {
    if (!session?.analysis_start_station && !session?.analysis_end_station) {
      return 'Full Session';
    }
    
    const startStation = session.analysis_start_station || session.start_station_ft || 0;
    const endStation = session.analysis_end_station || session.end_station_ft || 0;
    
    return formatDistance(startStation, endStation);
  };

  const handleDeleteSession = async () => {
    try {
      setDeleting(true);
      setDeleteError('');

      const result = await deleteSession(sessionId);
      
      navigate(`/projects/${result.projectId}?tab=overview`);
      
    } catch (err) {
      console.error('Error deleting session:', err);
      setDeleteError(err.message || 'Failed to delete session');
    } finally {
      setDeleting(false);
    }
  };

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValues({
      name: session.name || '',
      operator: session.operator || '',
      direction: session.direction || '',
      lane_no: session.lane_no || '',
      route_lrs_key: session.route_lrs_key || '',
      run_date: formatDateForInput(session.run_date) || '',
      profiler_model: session.profiler_model || '',
      meta: typeof session.meta === 'object' ? JSON.stringify(session.meta, null, 2) : (session.meta || ''),
      analysis_start_station: session.analysis_start_station ? formatStationFromFeet(session.analysis_start_station) : '',
      analysis_end_station: session.analysis_end_station ? formatStationFromFeet(session.analysis_end_station) : '',
    });
    
    setStationValidation({
      start: { isValid: true, error: null },
      end: { isValid: true, error: null }
    });
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      
      let startStationFeet = null;
      let endStationFeet = null;
      
      if (editValues.analysis_start_station?.trim()) {
        const startValidation = parseStationString(editValues.analysis_start_station);
        if (!startValidation.isValid) throw new Error(`Invalid start station: ${startValidation.error}`);
        startStationFeet = startValidation.feet;
      }
      
      if (editValues.analysis_end_station?.trim()) {
        const endValidation = parseStationString(editValues.analysis_end_station);
        if (!endValidation.isValid) throw new Error(`Invalid end station: ${endValidation.error}`);
        endStationFeet = endValidation.feet;
      }
      
      let metaValue = editValues.meta;
      if (typeof metaValue === 'string' && metaValue.trim().startsWith('{')) {
        try { metaValue = JSON.parse(metaValue); } catch {}
      }
      
      const sanitizeDate = (dateValue) => (!dateValue || dateValue === '') ? null : dateValue;
      
      const { error } = await supabase
        .from('prototype_sessions')
        .update({
          name: editValues.name,
          operator: editValues.operator,
          direction: editValues.direction,
          lane_no: editValues.lane_no,
          route_lrs_key: editValues.route_lrs_key,
          run_date: sanitizeDate(editValues.run_date),
          profiler_model: editValues.profiler_model,
          meta: metaValue,
          analysis_start_station: startStationFeet,
          analysis_end_station: endStationFeet,
        })
        .eq('session_id', sessionId);

      if (error) throw error;

      setSession(prev => ({
        ...prev,
        name: editValues.name, operator: editValues.operator, direction: editValues.direction, lane_no: editValues.lane_no,
        route_lrs_key: editValues.route_lrs_key, run_date: sanitizeDate(editValues.run_date), profiler_model: editValues.profiler_model,
        meta: metaValue, analysis_start_station: startStationFeet, analysis_end_station: endStationFeet,
      }));
      
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving session:', err);
      alert('Failed to save changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({ ...prev, [field]: value }));
  };

  const handleBackClick = () => {
    if (project?.id) {
      navigate(`/projects/${project.id}?tab=overview`);
    } else {
      navigate('/projects');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const formatDistance = (startFt, endFt) => {
    if (!startFt || !endFt) return 'N/A';
    const feet = Math.abs(endFt - startFt);
    return (feet >= 5280) ? `${(feet / 5280).toFixed(2)} mi` : `${feet.toLocaleString()} ft`;
  };

  // Calculate summary statistics using filtered data
  const totalSegments = filteredSegments.length;
  const totalEvents = filteredEvents.length;
  const totalDistance = filteredSegments.reduce((sum, segment) => sum + (segment.distance_ft || 0), 0);

  const allALREvents = useMemo(() => 
    (events || []).filter(e => 
      e.event_type === 'LR' && 
      (e.magnitude_in ?? 0) > (projectConfig?.iri_threshold_type_a ?? 0)
    ).sort((a, b) => a.from_station_ft - b.from_station_ft),
    [events, projectConfig]
  );

  const handleAlrRowClick = (alrData) => {
    const fullEvent = allALREvents.find(e => e.event_id === alrData.eventId);
    if (fullEvent) {
      setSelectedAlrEvent(fullEvent);
      setIsAlrModalOpen(true);
    } else {
      console.error("Could not find full ALR event data for ID:", alrData.eventId);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Session Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={handleBackClick} className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            <ArrowLeftIcon className="h-4 w-4 mr-2" /> Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Breadcrumb 
        items={breadcrumbItems} 
        loading={loading}
        tabs={session || loading ? tabsWithState : null}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading session data...</p>
              {loadingProgress && <p className="text-sm text-gray-500 mt-2">{loadingProgress}</p>}
            </div>
          </div>
        ) : !session ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-20">
              <h3 className="text-lg font-medium text-gray-900">Session not found</h3>
              <button onClick={handleBackClick} className="mt-2 text-red-600 hover:text-red-500 underline">
                ← Back to Project
              </button>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'map' ? (
  <div className="flex-1 relative">
    {(segments.length > 0 || events.length > 0 || filteredSegments.length > 0 || filteredEvents.length > 0) ? (
      <div className="absolute inset-0">
        <FusionMap
          sessionId={sessionId}
          segments={filteredSegments.length > 0 ? filteredSegments : segments}
          events={filteredEvents.length > 0 ? filteredEvents : events}
          bounds={mapBounds}
          session={session}
          projectConfig={projectConfig}
          // Add KML data props
          iriProfileData={iriProfileData}
          elevationProfileData={elevationProfileData}
          kmlLoading={kmlLoading}
          kmlError={kmlError}
          kmlMarkers={markers}
         onAnalyzeSegment={handleAnalyzeSegment}
        />
      </div>
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <MapIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg text-gray-600">No geographic data available</p>
        </div>
      </div>
    )}
  </div>
) : activeTab === 'alr' ? (
  <div className="flex-1 relative">
    <div className="absolute inset-0">
      <ALRMapView session={session} segments={filteredSegments} events={filteredEvents} projectConfig={projectConfig} kmlUrl={kmlUrl} />
    </div>
  </div>
) : activeTab === 'alr' ? (
              <div className="flex-1 w-full">
                <ALRMapView session={session} segments={filteredSegments} events={filteredEvents} projectConfig={projectConfig} kmlUrl={kmlUrl} />
              </div>
            ) : activeTab === 'analysis' ? (
              <div className="flex-1 overflow-auto">
                <div className="mx-auto">
                  <AnalysisPage
        embedded={true} 
        sessionData={session} 
        segmentsData={filteredSegments.length > 0 ? filteredSegments : segments}
        eventsData={filteredEvents.length > 0 ? filteredEvents : events} 
        projectData={project} 
        projectConfigData={projectConfig}
        payAnalysisData={payAnalysis} 
        payAnalysisLoading={payAnalysisLoading}
        onAlrRowClick={handleAlrRowClick}
        // Add KML data props
        iriProfileData={iriProfileData}
        elevationProfileData={elevationProfileData}
        kmlLoading={kmlLoading}
        kmlError={kmlError}
        targetSegmentId={targetSegmentId}
                    onClearTargetSegment={() => setTargetSegmentId(null)}
      />
                </div>
              </div>
            ) : activeTab === 'pay-analysis' ? (
              <div className="flex-1 overflow-auto">
                <div className="mx-auto px-4 py-4">
                  {analysisRangeActive && filterStats && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <InformationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-red-800">
                              Pay Analysis Range: {formatStationFromFeet(session.analysis_start_station)} - {formatStationFromFeet(session.analysis_end_station)}
                            </p>
                            <p className="text-xs text-red-600">
                              Analysis based on {filterStats.filteredSegments} segments ({getAnalysisDistance()})
                            </p>
                          </div>
                        </div>
                        <button onClick={handleClearAnalysisRange} className="text-xs text-red-600 hover:text-red-800 underline">
                          Clear Filter
                        </button>
                      </div>
                    </div>
                  )}

                  {!payAnalysisAvailable ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                      <ExclamationTriangleIcon className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                      <h2 className="text-xl font-bold text-gray-900 mb-2">Pay Analysis Unavailable</h2>
                      <p className="text-gray-600 mb-6">Project configuration or pay table is missing.</p>
                      <button onClick={() => navigate(`/projects/${project?.id}?tab=settings`)} className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                        <CogIcon className="h-4 w-4 mr-2" /> Configure Project
                      </button>
                    </div>
                  ) : payAnalysisLoading ? (
                    <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Calculating pay adjustments...</p>
                    </div>
                  ) : payAnalysis ? (
                    <PayAnalysisContent
                      payAnalysis={payAnalysis} 
                      session={session} 
                      project={project} 
                      projectConfig={projectConfig}
                      segments={filteredSegments.length > 0 ? filteredSegments : segments} 
                      payTableName={projectConfig?.pay_tables?.name || 'Unknown'}
                    />
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                      <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Pay Analysis Results</h3>
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'files' ? (
              // Render full-height tabs directly into the page's flex column so
              // they claim the remaining viewport. Skips the mx-auto/overflow
              // wrapper used by content-height tabs.
              <SessionFiles
                sessionId={sessionId}
                session={session}
                project={project}
                projectConfig={projectConfig}
                segments={filteredSegments.length > 0 ? filteredSegments : segments}
                events={filteredEvents.length > 0 ? filteredEvents : events}
              />
            ) : activeTab === 'photos' ? (
              <SitePhotosTab sessionId={sessionId} />
            ) : activeTab === 'details' ? (
              <SessionDetails
                session={session}
                project={project}
                projectConfig={projectConfig}
                filteredEvents={filteredEvents}
                totalDistance={totalDistance}
                totalSegments={totalSegments}
                totalEvents={totalEvents}
                payAnalysis={payAnalysis}
                payAnalysisAvailable={payAnalysisAvailable}
                isEditing={isEditing}
                editValues={editValues}
                saving={saving}
                handleEdit={handleEdit}
                handleCancelEdit={handleCancelEdit}
                handleSaveEdit={handleSaveEdit}
                handleInputChange={handleInputChange}
                setShowDeleteModal={setShowDeleteModal}
                setShowHQMode={setShowHQMode}
                handleTabChange={handleTabChange}
                formatDate={formatDate}
                formatDateForInput={formatDateForInput}
              />
            ) : activeTab === 'share' ? (
              <ShareTab
                session={session}
                sessionId={sessionId}
              />
            ) : null}
          </>
        )}
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Session</h3>
              </div>
              <button onClick={() => { setShowDeleteModal(false); setDeleteError(''); }} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">This action cannot be undone</p>
                    <p className="text-sm text-red-700 mt-1">This will permanently remove all associated segments, events, and files.</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-700">Please type the session name to confirm:</p>
                <p className="text-sm font-mono text-gray-900 bg-gray-100 p-2 rounded mt-1">
                  {session?.name || session?.filename || 'Unnamed Session'}
                </p>
                <input type="text" placeholder="Type session name here"
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  id="confirmDeleteInput"
                />
              </div>

              {deleteError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{deleteError}</p>
                </div>
              )}
            </div>

            <div className="flex space-x-3 p-6 border-t border-gray-200">
              <button onClick={() => { setShowDeleteModal(false); setDeleteError(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const confirmInput = document.getElementById('confirmDeleteInput');
                  if (confirmInput.value !== (session?.name || session?.filename || 'Unnamed Session')) {
                    setDeleteError('Session name does not match.');
                    return;
                  }
                  handleDeleteSession();
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      

      {isAlrModalOpen && selectedAlrEvent && (
  <ALRMapModal 
    alrEvent={selectedAlrEvent}
    allALREvents={allALREvents}
    events={filteredEvents.length > 0 ? filteredEvents : events}
    onClose={() => setIsAlrModalOpen(false)}
    
  />
)}

    </div>
  );
};

export default SessionPage;
